"use client";

import { logger } from "@/utils/logger";
import { storage } from "@/lib/storage";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useRef } from "react";
import { useStore } from "zustand";
import { BuyerState, createBuyerStore } from "./stores/buyer-store";
import { createSellerStore, SellerState } from "./stores/seller-store";
import { createUserStore, UserState, UserPreferences } from "./stores/user-store";
import { ComparisonState, createComparisonStore } from "./stores/comparison-store";
import { User } from "@/gql/auth";
import { SellerUnitAndProject } from "@/types/sup";
import { RawUnit } from "@/types/rawUnit";

export type BuyerStoreApi = ReturnType<typeof createBuyerStore>;
export type SellerStoreApi = ReturnType<typeof createSellerStore>;
export type UserStoreApi = ReturnType<typeof createUserStore>;
export type ComparisonStoreApi = ReturnType<typeof createComparisonStore>;

export interface UserProviderProps {
	children: ReactNode;
	initialFavorites?: SellerUnitAndProject[];
	initialUser?: User | null;
	initialUserPreferences?: Partial<UserPreferences>;
	initialSellerProperties?: RawUnit[];
}

export const BuyerStoreContext = createContext<BuyerStoreApi | undefined>(undefined);
export const SellerStoreContext = createContext<SellerStoreApi | undefined>(undefined);
export const UserStoreContext = createContext<UserStoreApi | undefined>(undefined);
export const ComparisonStoreContext = createContext<ComparisonStoreApi | undefined>(undefined);

export function UserProvider({
	children,
	initialFavorites,
	initialUser,
	initialUserPreferences,
	initialSellerProperties,
}: UserProviderProps) {
	const buyerStoreRef = useRef<BuyerStoreApi | null>(null);
	const sellerStoreRef = useRef<SellerStoreApi | null>(null);
	const userStoreRef = useRef<UserStoreApi | null>(null);
	const comparisonStoreRef = useRef<ComparisonStoreApi | null>(null);

	if (buyerStoreRef.current === null) {
		buyerStoreRef.current = createBuyerStore({
			initialFavorites,
		});
	}

	if (sellerStoreRef.current === null) {
		sellerStoreRef.current = createSellerStore({
			initialRawUnitAndProperties: initialSellerProperties,
		});
	}

	if (userStoreRef.current === null) {
		userStoreRef.current = createUserStore({
			initialUser,
			initialPreferences: initialUserPreferences,
		});
	}

	if (comparisonStoreRef.current === null) {
		comparisonStoreRef.current = createComparisonStore();
	}

	// Initialize data from storage on mount (client-side hydration)
	useEffect(() => {
		if (typeof window !== "undefined") {
			// Use requestIdleCallback to defer storage operations until after hydration
			const initializeFromStorage = async () => {
				try {
					// Initialize favorites if not already set
					if (!initialFavorites) {
						const storedFavorites = await storage.getItem<SellerUnitAndProject[]>("tray_properties");
						if (storedFavorites) {
							buyerStoreRef.current?.getState().initFavorites(storedFavorites);
						}
					}

					// Initialize comparison tray from IndexedDB
					const storedComparisonTray = await storage.getItem<SellerUnitAndProject[]>("comparison_tray");

					if (storedComparisonTray && Array.isArray(storedComparisonTray)) {
						comparisonStoreRef.current?.getState().initComparisonTray(storedComparisonTray);
					} else if (storedComparisonTray !== null && storedComparisonTray !== undefined) {
						// Log warning only if data exists but is invalid format (security concern)
						logger.warn("Invalid comparison tray data format in storage");
					}

					// Initialize user preferences if not already set
					if (!initialUserPreferences) {
						const storedPreferences = await storage.getItem<Partial<UserPreferences>>("settlin_user_preferences");
						if (storedPreferences) {
							userStoreRef.current?.getState().updatePreferences(storedPreferences);
						}
					}
				} catch (error) {
					logger.error("Error loading data from storage:", error);
				}
			};

			// Initialize immediately to ensure comparison tray data is available
			// Use microtask to avoid blocking initial render
			Promise.resolve().then(initializeFromStorage);
		}
	}, [initialFavorites, initialUserPreferences]);

	return (
		<BuyerStoreContext.Provider value={buyerStoreRef.current}>
			<SellerStoreContext.Provider value={sellerStoreRef.current}>
				<UserStoreContext.Provider value={userStoreRef.current}>
					<ComparisonStoreContext.Provider value={comparisonStoreRef.current}>
						{children}
					</ComparisonStoreContext.Provider>
				</UserStoreContext.Provider>
			</SellerStoreContext.Provider>
		</BuyerStoreContext.Provider>
	);
}

// Buyer store hooks
export function useBuyerStoreData<T>(selector: (state: BuyerState) => T): T {
	const store = useContext(BuyerStoreContext);
	if (!store) throw new Error("useBuyerStoreData must be used within UserProvider");
	return useStore(store, selector);
}

export function useBuyerStore(): BuyerStoreApi {
	const store = useContext(BuyerStoreContext);
	if (!store) throw new Error("useBuyerStore must be used within UserProvider");
	return store;
}

// Seller store hooks
export function useSellerStoreData<T>(selector: (state: SellerState) => T): T {
	const store = useContext(SellerStoreContext);
	if (!store) throw new Error("useSellerStoreData must be used within UserProvider");
	return useStore(store, selector);
}

export function useSellerStore(): SellerStoreApi {
	const store = useContext(SellerStoreContext);
	if (!store) throw new Error("useSellerStore must be used within UserProvider");
	return store;
}

// User store hooks
export function useUserStoreData<T>(selector: (state: UserState) => T): T {
	const store = useContext(UserStoreContext);
	if (!store) throw new Error("useUserStoreData must be used within UserProvider");
	return useStore(store, selector);
}

export function useUserStore(): UserStoreApi {
	const store = useContext(UserStoreContext);
	if (!store) throw new Error("useUserStore must be used within UserProvider");
	return store;
}

// Comparison store hooks
export function useComparisonStoreData<T>(selector: (state: ComparisonState) => T): T {
	const store = useContext(ComparisonStoreContext);
	if (!store) throw new Error("useComparisonStoreData must be used within UserProvider");
	return useStore(store, selector);
}

export function useComparisonStore(): ComparisonStoreApi {
	const store = useContext(ComparisonStoreContext);
	if (!store) throw new Error("useComparisonStore must be used within UserProvider");
	return store;
}
